import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ActionCreators from '../../redux/action';
import { FormattedMessage } from 'react-intl';
import { HeadingThree, BodyText } from '../typography';
import Button from '../ui/button';
import ContentWrap from '../content-wrap';
import InlineLoading from '../ui/inline-loading';
import DocumentCamera from '../document-camera';
import OnlineOnboardingContentSC from './online-onboarding-content.styled';
import { LS_ACCESS_TOKEN } from '../../constants';
import WishmakerModal from '../ui/modal/modal';
import DocsModal from '../ui/modal/DocsModal';
import SignatureModal from '../ui/modal/SignatureModal';
import numeral from 'numeral';
import { ocrFormSchema } from './ocr-form-rules';

// Global variables for ID card section visibility
let showFrontImage = true;
let showBackImage = true;
let showSelfieImage = true;

// Global variable for status name from backend
let statusName = null;

// Global variables for income document section visibility
let showPayslip1 = true;
let showPayslip2 = true;
let showBankStatement1 = true;
let showBankStatement2 = true;

const _OnlineOnboardingContent = ({
  currentStep,
  setCurrentStep,
  getBorrowerOnboardingStep,
  onBoardingStep,
  onBoardingStepData,
  sendBorrowerOnboardingDocuments,
  sendBorrowerOnboardingStep,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // Flag pre zastavenie polling-u počas POST requestu
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [ocrErrors, setOcrErrors] = useState({});
  const [useDifferentContactAddress, setUseDifferentContactAddress] = useState(false);

  // Refs pre sledovanie predchádzajúceho statusu a kroku
  const prevStatusRef = useRef(null);
  const prevStepRef = useRef(null);

  // Income documents state
  const [payslip1, setPayslip1] = useState(null);
  const [payslip2, setPayslip2] = useState(null);
  const [bankStatement1, setBankStatement1] = useState(null);
  const [bankStatement2, setBankStatement2] = useState(null);
  // Keys to reset file inputs when clearing selection
  const [payslip1Key, setPayslip1Key] = useState(0);
  const [payslip2Key, setPayslip2Key] = useState(0);
  const [bankStatement1Key, setBankStatement1Key] = useState(0);
  const [bankStatement2Key, setBankStatement2Key] = useState(0);

  // Error messages for income documents
  const [payslip1Error, setPayslip1Error] = useState('');
  const [payslip2Error, setPayslip2Error] = useState('');
  const [bankStatement1Error, setBankStatement1Error] = useState('');
  const [bankStatement2Error, setBankStatement2Error] = useState('');

  // Loan documentation state
  const [loanDocuments, setLoanDocuments] = useState([]);
  const [areAllDocumentsRead, setAreAllDocumentsRead] = useState(false);
  const [agreesToDocuments, setAgreesToDocuments] = useState(false);
  const [docsLoadedForStep, setDocsLoadedForStep] = useState(null); // Sleduje, pre ktorý krok sa už načítali dokumenty

  // Redirect countdown state
  const [redirectCountdown, setRedirectCountdown] = useState(10);

  // State for document modal
  const [isDocsModalOpen, setDocsModalOpen] = useState(false);
  const [docsModalIndex, setDocsModalIndex] = useState(0);
  const [hoveredDoc, setHoveredDoc] = useState(null);

  // Signature modal state
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false);

  // Insurance selection modal state
  const [isInsuranceModalOpen, setInsuranceModalOpen] = useState(false);
  const [selectedInsuranceVariant, setSelectedInsuranceVariant] = useState(null); // number | 'none' | null

  // Load onboarding data from API
  useEffect(() => {
    getBorrowerOnboardingStep();
  }, [getBorrowerOnboardingStep]);

  // Update currentStep based on API response
  useEffect(() => {
    if (onBoardingStep !== null && onBoardingStep !== undefined) {
      setCurrentStep(onBoardingStep);
      setIsInitialLoading(false);
    }
  }, [onBoardingStep, setCurrentStep]);

  // Set checkbox if backend returns contact address data or has_different_contact_address flag
  // Aktualizuj len pri zmene statusu alebo kroku
  useEffect(() => {
    const currentStatus = onBoardingStepData?.status?.name;
    const currentStep = onBoardingStepData?.step;

    // Skontroluj, či sa zmenil status alebo krok
    const statusChanged = prevStatusRef.current !== null && prevStatusRef.current !== currentStatus;
    const stepChanged = prevStepRef.current !== null && prevStepRef.current !== currentStep;
    const isFirstLoad = prevStatusRef.current === null && prevStepRef.current === null;

    if (onBoardingStepData?.data && (isFirstLoad || statusChanged || stepChanged)) {
      // Vyplniť základné polia z backendu
      setOcrData((prev) => ({
        ...prev,
        ...onBoardingStepData.data,
        // Ak existuje kontaktna adresa, rozbal ju do ocrData
        ...(onBoardingStepData.data.contact ? {
          contact_address: onBoardingStepData.data.contact.address || '',
          contact_city: onBoardingStepData.data.contact.city || '',
          contact_postal_code: onBoardingStepData.data.contact.postal_code || '',
          contact_indicative_number: onBoardingStepData.data.contact.indicative_number || '',
          contact_statement_number: onBoardingStepData.data.contact.statement_number || '',
          has_different_contact_address: !!onBoardingStepData.data.contact.address,
        } : {})
      }));
      // Nastaviť checkbox pre kontaktnú adresu
      const hasContactAddress =
        onBoardingStepData.data.contact?.address ||
        onBoardingStepData.data.contact?.city ||
        onBoardingStepData.data.contact?.postal_code;
      setUseDifferentContactAddress(!!hasContactAddress);
    }

    // Aktualizuj refs na konci, ale mimo if bloku - vždy sa musia aktualizovať
    prevStatusRef.current = currentStatus;
    prevStepRef.current = currentStep;
  }, [onBoardingStepData]);

  // Function for handling id-card step with new Redux action for documents
  const handleIdCardStep = () => {
    // Validate only images required by options
    if ((showFrontImage && !frontImage) || (showBackImage && !backImage) || (showSelfieImage && !selfieImage)) {
      alert('Prosím, nahrajte všetky požadované časti občianskeho preukazu.');
      return;
    }

    setIsLoading(true);
    setIsSubmitting(true); // Zastav polling počas POST requestu

    // Create FormData for file upload
    const formData = new FormData();
    if (showFrontImage) formData.append('front_image', frontImage);
    if (showBackImage) formData.append('back_image', backImage);
    if (showSelfieImage) formData.append('selfie_image', selfieImage);
    formData.append('step', 'id-card');

    // Use new Redux action for documents with multipart/form-data
    sendBorrowerOnboardingDocuments({
      data: formData,
      onSuccess: () => {
        console.log('Úspešne odoslané dokumenty');
        setIsLoading(false);
        setIsSubmitting(false); // Obnov polling
        // Po úspešnom odoslaní obnovíme onboarding stav
        getBorrowerOnboardingStep();
      },
      onError: () => {
        console.error('Chyba pri odoslaní dokumentov');
        setIsLoading(false);
        setIsSubmitting(false); // Obnov polling
        alert('Nastala chyba pri odoslaní dokumentov. Skúste to znovu.');
      },
    });
  };

  // Function for handling changes in OCR form
  const handleOcrChange = (field, value) => {
    setOcrData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper: validate date format dd.mm.yyyy
  const isValidDateFormat = (value) => {
    if (!value) return true;
    return /^\d{2}\.\d{2}\.\d{4}$/.test(value);
  };

  // Custom handler for date fields
  const handleOcrDateChange = (field, value) => {
    // Povoliť len čísla a bodky, max 10 znakov
    let filtered = value.replace(/[^0-9.]/g, '').slice(0, 10);
    // Ak je dĺžka 10, skontroluj formát
    if (filtered.length === 10 && !isValidDateFormat(filtered)) {
      return; // neulož, ak nie je správny formát
    }
    setOcrData((prev) => ({ ...prev, [field]: filtered }));
  };

  // Function for submitting OCR data
  const handleOcrStep = () => {
    // List of all fields to send in OCR step
    const ocrFields = [
      'first_name',
      'last_name',
      'birth_name',
      'birth_date',
      'birth_place',
      'personal_identification_number',
      'sex',
      'nationality',
      'address',
      'indicative_number',
      'statement_number',
      'city',
      'postal_code',
      'document_number',
      'issue_date',
      'expiry_date',
      'issued_by',
      'special_remarks',
      'has_different_contact_address', // always included
    ];
    if (useDifferentContactAddress) {
      ocrFields.push(
        'contact_address',
        'contact_indicative_number',
        'contact_statement_number',
        'contact_city',
        'contact_postal_code'
      );
    }

    // Prepare form data for validation
    const formDataForValidation = {};
    ocrFields.forEach((field) => {
      formDataForValidation[field] = getFormValue(field);
    });

    // Yup validation
    ocrFormSchema
      .validate(formDataForValidation, { abortEarly: false })
      .then(() => {
        setOcrErrors({});
        setIsLoading(true);
        setIsSubmitting(true); // Zastav polling počas POST requestu
        // Create payload with all values (priority: ocrData -> onBoardingStepData.data -> '')
        const formData = { step: 'ocr' };
        ocrFields.forEach((field) => {
          formData[field] = getFormValue(field);
        });
        sendBorrowerOnboardingStep({
          data: formData,
          onSuccess: () => {
            setIsLoading(false);
            setIsSubmitting(false); // Obnov polling
            const statusNameLocal = onBoardingStepData?.status?.name;
            if (statusNameLocal !== 'online_supplement_pre_loan') {
              setCurrentStep('income-docs');
            }
            getBorrowerOnboardingStep();
          },
          onError: () => {
            setIsLoading(false);
            setIsSubmitting(false); // Obnov polling
            alert('Nastala chyba pri odoslaní údajov. Skúste to znovu.');
          },
        });
      })
      .catch((err) => {
        if (err && err.inner) {
          const errors = {};
          err.inner.forEach((e) => {
            // Ak existuje viac chýb pre jedno pole, ulož ich do poľa
            if (!errors[e.path]) {
              errors[e.path] = [];
            }
            errors[e.path].push(e.message);
          });
          setOcrErrors(errors);
        }
      });
  };

  // Function for handling income documents step
  const handleIncomeDocsStep = () => {
    // New logic: Open insurance modal only if backend says insured === false
    if (onBoardingStepData?.insured === false) {
      // Open only if not already open
      if (!isInsuranceModalOpen) setInsuranceModalOpen(true);
      return;
    }
    submitIncomeDocs();
  };

  // Extracted submission logic (formerly tail of handleIncomeDocsStep)
  const submitIncomeDocs = () => {
    // Validate only documents required by options
    if ((showPayslip1 && !payslip1) || (showPayslip2 && !payslip2) || (showBankStatement1 && !bankStatement1) || (showBankStatement2 && !bankStatement2)) {
      alert('Prosím, nahrajte všetky požadované dokumenty.');
      return;
    }

    setIsLoading(true);
    setIsSubmitting(true); // Zastav polling počas POST requestu

    // Create FormData for file upload
    const formData = new FormData();
    if (showPayslip1 && payslip1) formData.append('payslip_1', payslip1);
    if (showPayslip2 && payslip2) formData.append('payslip_2', payslip2);
    if (showBankStatement1 && bankStatement1) formData.append('bank_statement_1', bankStatement1);
    if (showBankStatement2 && bankStatement2) formData.append('bank_statement_2', bankStatement2);
    if (typeof selectedInsuranceVariant === 'number') formData.append('insurance_variant_id', String(selectedInsuranceVariant));
    formData.append('step', 'income-docs');

    sendBorrowerOnboardingDocuments({
      data: formData,
      onSuccess: () => {
        console.log('Úspešne odoslané dokumenty o príjmoch');
        setIsLoading(false);
        setIsSubmitting(false); // Obnov polling
        // Po úspešnom odoslaní obnovíme onboarding stav
        getBorrowerOnboardingStep();
      },
      onError: () => {
        console.error('Chyba pri odoslaní dokumentov o príjmoch');
        setIsLoading(false);
        setIsSubmitting(false); // Obnov polling
        alert('Nastala chyba pri odoslaní dokumentov. Skúste to znovu.');
      },
    });
  };

  // Validation function for income documents
  const validateIncomeDocument = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword', // DOC
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'application/vnd.ms-excel', // XLS
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
    ];

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes

    if (!file) {
      return { isValid: false, message: 'Žiadny súbor nebol vybratý' };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        message: 'Nepodporovaný typ súboru. Podporované sú: PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX',
      };
    }

    if (file.size > maxSize) {
      return {
        isValid: false,
        message: 'Súbor je príliš veľký. Maximálna veľkosť je 5MB',
      };
    }

    // Check for duplicate files
    const allIncomeFiles = [payslip1, payslip2, bankStatement1, bankStatement2];
    const isDuplicate = allIncomeFiles.some((existingFile) => {
      if (!existingFile) return false;
      // Compare file name, size, type, and last modified date
      return (
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.type === file.type &&
        existingFile.lastModified === file.lastModified
      );
    });

    if (isDuplicate) {
      return {
        isValid: false,
        message: 'Tento súbor už bol nahratý. Prosím, vyberte iný súbor.',
      };
    }

    return { isValid: true, message: '' };
  };

  const handleDocumentCapture = (file, documentType) => {
    if (documentType === 'payslip1') {
      setPayslip1Error('');
    } else if (documentType === 'payslip2') {
      setPayslip2Error('');
    } else if (documentType === 'bankStatement1') {
      setBankStatement1Error('');
    } else if (documentType === 'bankStatement2') {
      setBankStatement2Error('');
    }

    if (['payslip1', 'payslip2', 'bankStatement1', 'bankStatement2'].includes(documentType)) {
      const validation = validateIncomeDocument(file);
      if (!validation.isValid) {
        if (documentType === 'payslip1') {
          setPayslip1Error(validation.message);
        } else if (documentType === 'payslip2') {
          setPayslip2Error(validation.message);
        } else if (documentType === 'bankStatement1') {
          setBankStatement1Error(validation.message);
        } else if (documentType === 'bankStatement2') {
          setBankStatement2Error(validation.message);
        }
        return;
      }
    }

    if (documentType === 'front') {
      setFrontImage(file);
    } else if (documentType === 'back') {
      setBackImage(file);
    } else if (documentType === 'selfie') {
      setSelfieImage(file);
    } else if (documentType === 'payslip1') {
      setPayslip1(file);
    } else if (documentType === 'payslip2') {
      setPayslip2(file);
    } else if (documentType === 'bankStatement1') {
      setBankStatement1(file);
    } else if (documentType === 'bankStatement2') {
      setBankStatement2(file);
    }
  };

  // Helper to remove selected income document and reset file input
  const handleRemoveIncomeDoc = (documentType) => {
    if (isLoading) return; // prevent changes during submission
    switch (documentType) {
      case 'payslip1':
        setPayslip1(null);
        setPayslip1Error('');
        setPayslip1Key((k) => k + 1);
        break;
      case 'payslip2':
        setPayslip2(null);
        setPayslip2Error('');
        setPayslip2Key((k) => k + 1);
        break;
      case 'bankStatement1':
        setBankStatement1(null);
        setBankStatement1Error('');
        setBankStatement1Key((k) => k + 1);
        break;
      case 'bankStatement2':
        setBankStatement2(null);
        setBankStatement2Error('');
        setBankStatement2Key((k) => k + 1);
        break;
      default:
        break;
    }
  };

  // Function to get value from form (combines original data with edits)
  // In getFormValue, always return boolean for has_different_contact_address
  const getFormValue = (field) => {
    if (field === 'has_different_contact_address') {
      // Always return boolean
      return !!useDifferentContactAddress;
    }
    let value = ocrData[field];
    if (value === undefined) {
      value = onBoardingStepData?.data?.[field];
    }
    // Ak je null, undefined alebo string 'null', vrat prázdny string (okrem boolean)
    if (value === null || value === undefined || value === 'null') return '';
    return value;
  };

  // State keys for special states
  const specialStates = [
    'online_rejected',
    'online_canceled_framework_contract',
    'online_canceled_by_wm',
    'online_canceled_by_client',
    'online_refused_by_wm',
    'online_refused_by_client',
    'online_request_canceled_by_wm',
    'online_request_canceled_by_client',
    'online_request_not_finished',
  ];

  // Helper function to determine special state name from various sources (hoisted)
  function getSpecialStateName() {
    const statusName = onBoardingStepData?.status?.name;
    if (typeof statusName === 'string' && specialStates.includes(statusName)) {
      return statusName;
    }
    return null;
  }

  // Calculate special state once per render (must be before useEffect below)
  const specialStateName = getSpecialStateName();

  // Polling getBorrowerOnboardingStep každých 10 sekúnd, iba ak NIE JE špeciálny stav a NESPÚŠŤA SA POST request
  useEffect(() => {
    if (specialStateName || isSubmitting) return; // Ak je špeciálny stav alebo prebieha POST, polling netreba
    const interval = setInterval(() => {
      getBorrowerOnboardingStep();
    }, 10000);
    return () => clearInterval(interval);
  }, [specialStateName, isSubmitting, getBorrowerOnboardingStep]);

  // Load loan documents when in get-loan-documentation step
  // Používa state flag pre sledovanie načítaných dokumentov, aby nekolidoval s OCR ref-mi
  useEffect(() => {
    const currentStepFromData = onBoardingStepData?.step || currentStep;

    const isLoanDocsStep = currentStepFromData === 'get-loan-documentation';
    const isInsuranceDocsStep = currentStepFromData === 'get-insurance-documentation';
    const isDocsStep = isLoanDocsStep || isInsuranceDocsStep;

    // Načítaj dokumenty len ak:
    // 1. Sme v kroku dokumentácie
    // 2. Dokumenty existujú
    // 3. Ešte sme nenačítali dokumenty pre tento krok
    if (isDocsStep && onBoardingStepData?.documents && docsLoadedForStep !== currentStepFromData) {
      setLoanDocuments(onBoardingStepData.documents.map((doc) => ({ ...doc, active: false })));
      setAreAllDocumentsRead(false);
      setAgreesToDocuments(false);
      setDocsLoadedForStep(currentStepFromData);
    }

    // Ak opustíme krok dokumentácie, resetuj flag
    if (!isDocsStep && docsLoadedForStep !== null) {
      setDocsLoadedForStep(null);
    }
  }, [currentStep, onBoardingStepData, docsLoadedForStep]);

  // Default insurance variant selection: if backend says insured === false and variant with id 2 exists and is active, preselect it
  useEffect(() => {
    if (onBoardingStepData?.insured === false && selectedInsuranceVariant === null) {
      const variants = Array.isArray(onBoardingStepData?.insurance_variants)
        ? onBoardingStepData.insurance_variants.filter(v => v && v.active)
        : [];
      const defaultVariant = variants.find(v => v.id === 2);
      if (defaultVariant) {
        setSelectedInsuranceVariant(2);
      }
    }
  }, [onBoardingStepData, selectedInsuranceVariant]);

  // Functions for loan documentation step
  const handleDocumentClick = (docId) => {
    // Find the index of the clicked document
    const docIndex = loanDocuments.findIndex((doc) => doc.id === docId);

    if (docIndex !== -1) {
      // Set the index and open the modal
      setDocsModalIndex(docIndex);
      setDocsModalOpen(true);
    }
  };

  const handleLoanDocumentationContinue = () => {
    if (!areAllDocumentsRead || !agreesToDocuments) return;
    if (
      currentStep === 'get-loan-documentation' ||
      currentStep === 'get-insurance-documentation'
    ) {
      setSignatureModalOpen(true);
    }
  };

  const handleSignatureSubmit = async (dataUrl, done) => {
    setIsSubmitting(true); // Zastav polling počas POST requestu
    // Determine correct step based on current step
    const step = currentStep === 'get-insurance-documentation' ? 'get-insurance-documentation' : 'get-loan-documentation';
    // Dynamicky nastav accepted_agreement_on_wage_deductions
    const acceptedAgreementOnWageDeductions = loanDocuments.some(doc =>
      ((doc.name && doc.name.toLowerCase().includes('zrazkach')) ||
       (doc.display_name && doc.display_name.toLowerCase().includes('zrazkach')))
      && doc.active === true
    );
    sendBorrowerOnboardingStep({
      data: { signature: dataUrl, step, accepted_agreement_on_wage_deductions: acceptedAgreementOnWageDeductions },
      onSuccess: () => {
        setIsSubmitting(false); // Obnov polling
        getBorrowerOnboardingStep();
        if (done) done();
        setSignatureModalOpen(false);
      },
      onError: () => {
        setIsSubmitting(false); // Obnov polling
        alert('Nastala chyba pri odoslaní podpisu. Skúste to znovu.');
        if (done) done();
      },
    });
  };


  // Display screen for special state
  const renderSpecialState = (stateName) => {
    let msgId = '';
    switch (stateName) {
      case 'online_rejected':
        msgId = 'online_onboarding_state_rejected';
        break;
      case 'online_canceled_framework_contract':
        msgId = 'online_onboarding_state_canceled_framework_contract';
        break;
      case 'online_canceled_by_wm':
        msgId = 'online_onboarding_state_canceled_by_wm';
        break;
      case 'online_canceled_by_client':
        msgId = 'online_onboarding_state_canceled_by_client';
        break;
      case 'online_refused_by_wm':
        msgId = 'online_onboarding_state_refused_by_wm';
        break;
      case 'online_refused_by_client':
        msgId = 'online_onboarding_state_refused_by_client';
        break;
      case 'online_request_canceled_by_wm':
        msgId = 'online_onboarding_state_request_canceled_by_wm';
        break;
      case 'online_request_canceled_by_client':
        msgId = 'online_onboarding_state_request_canceled_by_client';
        break;
      case 'online_request_not_finished':
        msgId = 'online_onboarding_state_request_not_finished';
        break;
      default:
        msgId = 'online_onboarding_state_unknown';
    }
    return (
      <div className="step-container">
        <HeadingThree>
          <FormattedMessage id={msgId} />
        </HeadingThree>
        <BodyText style={{ marginTop: 16 }}>
          <FormattedMessage
            id="online_onboarding_state_contact"
          />{' '}
          <a href="https://www.wishmaker.sk/kontakt" target="_blank" rel="noopener noreferrer">
            www.wishmaker.sk/kontakt
          </a>
        </BodyText>
      </div>
    );
  };

  // Auto-refresh after finished step (live countdown)
  useEffect(() => {
    if (currentStep === 'online-loan-finished' && !specialStateName) {
      setRedirectCountdown(10); // reset when entering step
    }
  }, [currentStep, specialStateName]);

  useEffect(() => {
    if (currentStep !== 'online-loan-finished' || specialStateName) return;
    if (redirectCountdown <= 0) {
      window.location.reload();
      return;
    }
    const t = setTimeout(() => setRedirectCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [currentStep, redirectCountdown, specialStateName]);

  // Helper: safely get options for section from supplementSteps (array or object)
  function getSectionOptions(supplementSteps, sectionKey) {
    if (!supplementSteps) return null;
    if (Array.isArray(supplementSteps)) {
      const section = supplementSteps.find((s) => s && s[sectionKey]);
      const opts = section && section[sectionKey] && section[sectionKey].options;
      return Array.isArray(opts) ? opts : null;
    }
    if (typeof supplementSteps === 'object') {
      const direct = supplementSteps[sectionKey];
      if (direct && Array.isArray(direct.options)) return direct.options;
      for (const v of Object.values(supplementSteps)) {
        if (v && v[sectionKey] && Array.isArray(v[sectionKey].options)) return v[sectionKey].options;
      }
    }
    return null;
  }

  // Function to set global variables for ID card section based on supplementSteps and status
  function setIdCardVisibilityVars(supplementSteps, status) {
    // Default all true
    showFrontImage = true;
    showBackImage = true;
    showSelfieImage = true;
    // If special state, set according to backend
    const isPreLoan = status?.name === 'online_supplement_pre_loan' || status === 'online_supplement_pre_loan';
    if (isPreLoan) {
      const options = getSectionOptions(supplementSteps, 'id-card');
      if (Array.isArray(options)) {
        showFrontImage = options.includes('front_image');
        showBackImage = options.includes('back_image');
        showSelfieImage = options.includes('selfie_image');
      }
    }
  }

  // Function to set global variables for income documents section based on supplementSteps and status
  function setIncomeDocsVisibilityVars(supplementSteps, status) {
    // Default all true
    showPayslip1 = true;
    showPayslip2 = true;
    showBankStatement1 = true;
    showBankStatement2 = true;
    // If special state, set according to backend
    const isPreLoan = status?.name === 'online_supplement_pre_loan' || status === 'online_supplement_pre_loan';
    if (isPreLoan) {
      const options = getSectionOptions(supplementSteps, 'income-docs');
      if (Array.isArray(options)) {
        showPayslip1 = options.includes('payslip_1');
        showPayslip2 = options.includes('payslip_2');
        showBankStatement1 = options.includes('bank_statement_1');
        showBankStatement2 = options.includes('bank_statement_2');
      }
    }
  }

  // In main component body, set variables according to backend
  // Set global statusName for consistent use across component
  statusName = (onBoardingStepData?.status && (onBoardingStepData.status.name || (typeof onBoardingStepData.status === 'string' ? onBoardingStepData.status : null)))
    || onBoardingStepData?.name
    || onBoardingStepData?.state
    || onBoardingStepData?.step
    || onBoardingStep
    || null;

  setIdCardVisibilityVars(
    onBoardingStepData?.supplementSteps,
    statusName
  );
  setIncomeDocsVisibilityVars(
    onBoardingStepData?.supplementSteps,
    statusName
  );

  if (isInitialLoading) {
    return (
      <ContentWrap>
        <OnlineOnboardingContentSC>
          <div className="online-onboarding-wrap">
            <div className="step-container">
              <InlineLoading />
              <BodyText style={{ textAlign: 'center', marginTop: '20px' }}>
                <FormattedMessage
                  id="online_onboarding_loading"
                  defaultMessage="Načítavame Vaše údaje..."
                />
              </BodyText>
            </div>
          </div>
        </OnlineOnboardingContentSC>
      </ContentWrap>
    );
  }

  const renderIdCardStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_id_card_title"
          defaultMessage="Overenie totožnosti"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_id_card_description"
          defaultMessage="Prosím, odfotografujte alebo nahrajte prednú a zadnú stranu Vášho občianskeho preukazu."
        />
      </BodyText>
      <div className="step-content">
        <BodyText>
          <FormattedMessage
            id="online_onboarding_id_card_info"
            defaultMessage="Pre dokončenie registrácie potrebujeme overiť Vašu identitu pomocou občianskeho preukazu. Uistite sa, že sú všetky údaje na dokumente dobre viditeľné."
          />
        </BodyText>
        <div className="documents-upload">
          {showFrontImage && (
            <DocumentCamera
              onCapture={handleDocumentCapture}
              documentType="front"
              isCapturing={isLoading}
            />
          )}
          {showBackImage && (
            <DocumentCamera
              onCapture={handleDocumentCapture}
              documentType="back"
              isCapturing={isLoading}
            />
          )}
          {showSelfieImage && (
            <DocumentCamera
              onCapture={handleDocumentCapture}
              documentType="selfie"
              isCapturing={isLoading}
              hideFileUpload={true}
            />
          )}
        </div>
        {((showFrontImage && !frontImage) || (showBackImage && !backImage) || (showSelfieImage && !selfieImage)) && (
          <BodyText style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
            <FormattedMessage
              id="online_onboarding_id_card_requirement"
              defaultMessage="Pre pokračovanie je potrebné nahrať požadované časti občianskeho preukazu"
            />
          </BodyText>
        )}
      </div>
      <div className="step-actions">
        <Button
          type="PRIMARY"
          onClick={handleIdCardStep}
          disabled={
            isLoading ||
            (showFrontImage && !frontImage) ||
            (showBackImage && !backImage) ||
            (showSelfieImage && !selfieImage)
          }
        >
          <FormattedMessage
            id="online_onboarding_id_card_continue"
            defaultMessage="Pokračovať"
          />
        </Button>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
            <InlineLoading />
          </div>
        )}
      </div>
    </div>
  );

  const renderOcrStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage id="online_onboarding_ocr_title" defaultMessage="Overenie údajov" />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_ocr_description"
          defaultMessage="Prosím, skontrolujte a upravte údaje z Vášho občianskeho preukazu."
        />
      </BodyText>

      <div className="step-content">
        <div className="ocr-form">
          {/* Sekcia 1: Osobné údaje */}
          <div className="form-section">
            <h4 className="section-title">Osobné údaje</h4>
            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_first_name" defaultMessage="Meno" />
                </label>
                <input
                  type="text"
                  value={getFormValue('first_name')}
                  onChange={(e) => handleOcrChange('first_name', e.target.value)}
                  style={ocrErrors['first_name'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['first_name'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['first_name'])
                      ? ocrErrors['first_name'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['first_name']} defaultMessage={ocrErrors['first_name']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_last_name" defaultMessage="Priezvisko" />
                </label>
                <input
                  type="text"
                  value={getFormValue('last_name')}
                  onChange={(e) => handleOcrChange('last_name', e.target.value)}
                  style={ocrErrors['last_name'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['last_name'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['last_name'])
                      ? ocrErrors['last_name'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['last_name']} defaultMessage={ocrErrors['last_name']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_birth_name" defaultMessage="Rodné meno" />
                  <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>
                    (<FormattedMessage id="ocr_form_optional" defaultMessage="nepovinné" />)
                  </span>
                </label>
                <input
                  type="text"
                  value={getFormValue('birth_name')}
                  onChange={(e) => handleOcrChange('birth_name', e.target.value)}
                  style={ocrErrors['birth_name'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['birth_name'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['birth_name'])
                      ? ocrErrors['birth_name'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['birth_name']} defaultMessage={ocrErrors['birth_name']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_birth_date" defaultMessage="Dátum narodenia" />
                </label>
                <input
                  type="text"
                  value={getFormValue('birth_date')}
                  onChange={(e) => handleOcrDateChange('birth_date', e.target.value)}
                  placeholder="dd.mm.yyyy"
                  style={ocrErrors['birth_date'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['birth_date'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['birth_date'])
                      ? ocrErrors['birth_date'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['birth_date']} defaultMessage={ocrErrors['birth_date']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_birth_place" defaultMessage="Miesto narodenia" />
                </label>
                <input
                  type="text"
                  value={getFormValue('birth_place')}
                  onChange={(e) => handleOcrChange('birth_place', e.target.value)}
                  style={ocrErrors['birth_place'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['birth_place'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['birth_place'])
                      ? ocrErrors['birth_place'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['birth_place']} defaultMessage={ocrErrors['birth_place']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_personal_identification_number" defaultMessage="Rodné číslo" />
                </label>
                <input
                  type="text"
                  value={getFormValue('personal_identification_number')}
                  onChange={(e) =>
                    handleOcrChange('personal_identification_number', e.target.value)
                  }
                  style={ocrErrors['personal_identification_number'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['personal_identification_number'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['personal_identification_number'])
                      ? ocrErrors['personal_identification_number'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['personal_identification_number']} defaultMessage={ocrErrors['personal_identification_number']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_sex" defaultMessage="Pohlavie" />
                </label>
                <input
                  type="text"
                  value={getFormValue('sex')}
                  onChange={(e) => handleOcrChange('sex', e.target.value)}
                  style={ocrErrors['sex'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['sex'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['sex'])
                      ? ocrErrors['sex'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['sex']} defaultMessage={ocrErrors['sex']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_nationality" defaultMessage="Štátna príslušnosť" />
                </label>
                <input
                  type="text"
                  value={getFormValue('nationality')}
                  onChange={(e) => handleOcrChange('nationality', e.target.value)}
                  style={ocrErrors['nationality'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['nationality'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['nationality'])
                      ? ocrErrors['nationality'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['nationality']} defaultMessage={ocrErrors['nationality']} />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sekcia 2: Adresa */}
          <div className="form-section">
            <h4 className="section-title">Adresa</h4>
            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_address" defaultMessage="Adresa" />
                </label>
                <input
                  type="text"
                  value={getFormValue('address')}
                  onChange={(e) => handleOcrChange('address', e.target.value)}
                  style={ocrErrors['address'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['address'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['address'])
                      ? ocrErrors['address'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['address']} defaultMessage={ocrErrors['address']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_indicative_number" defaultMessage="Orientačné číslo" />
                </label>
                <input
                  type="text"
                  value={getFormValue('indicative_number')}
                  onChange={(e) => handleOcrChange('indicative_number', e.target.value)}
                  style={ocrErrors['indicative_number'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['indicative_number'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['indicative_number'])
                      ? ocrErrors['indicative_number'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['indicative_number']} defaultMessage={ocrErrors['indicative_number']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_statement_number" defaultMessage="Súpisné číslo" />
                  <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>
                    (<FormattedMessage id="ocr_form_optional" defaultMessage="nepovinné" />)
                  </span>
                </label>
                <input
                  type="text"
                  value={getFormValue('statement_number')}
                  onChange={(e) => handleOcrChange('statement_number', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_city" defaultMessage="Mesto" />
                </label>
                <input
                  type="text"
                  value={getFormValue('city')}
                  onChange={(e) => handleOcrChange('city', e.target.value)}
                  style={ocrErrors['city'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['city'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['city'])
                      ? ocrErrors['city'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['city']} defaultMessage={ocrErrors['city']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_postal_code" defaultMessage="PSČ" />
                </label>
                <input
                  type="text"
                  value={getFormValue('postal_code')}
                  onChange={(e) => handleOcrChange('postal_code', e.target.value)}
                  style={ocrErrors['postal_code'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['postal_code'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['postal_code'])
                      ? ocrErrors['postal_code'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['postal_code']} defaultMessage={ocrErrors['postal_code']} />}
                  </div>
                )}
              </div>
              <div className="form-field">{/* Prázdne pole pre symetriu */}</div>
            </div>

            {/* Checkbox pre kontaktnú adresu */}
            <div className="form-row form-row-full" style={{ marginTop: '16px' }}>
              <div className="form-field">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={useDifferentContactAddress}
                    onChange={(e) => setUseDifferentContactAddress(e.target.checked)}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <FormattedMessage
                    id="ocr_form_use_different_contact_address"
                    defaultMessage="Používam inú kontaktnú adresu"
                  />
                </label>
                {ocrErrors['has_different_contact_address'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['has_different_contact_address'])
                      ? ocrErrors['has_different_contact_address'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['has_different_contact_address']} defaultMessage={ocrErrors['has_different_contact_address']} />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sekcia 2b: Kontaktná adresa (zobrazí sa len ak je checkbox zaškrtnutý) */}
          {useDifferentContactAddress && (
            <div className="form-section">
              <h4 className="section-title">
                <FormattedMessage id="ocr_form_contact_address_title" defaultMessage="Kontaktná adresa" />
              </h4>
              <div className="form-row">
                <div className="form-field">
                  <label>
                    <FormattedMessage id="ocr_form_contact_address" defaultMessage="Adresa" />
                  </label>
                  <input
                    type="text"
                    value={getFormValue('contact_address')}
                    onChange={(e) => handleOcrChange('contact_address', e.target.value)}
                    style={ocrErrors['contact_address'] ? { borderColor: 'red' } : {}}
                  />
                  {ocrErrors['contact_address'] && (
                    <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                      {Array.isArray(ocrErrors['contact_address'])
                        ? ocrErrors['contact_address'].map((msg, idx) => (
                            <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                          ))
                        : <FormattedMessage id={ocrErrors['contact_address']} defaultMessage={ocrErrors['contact_address']} />}
                    </div>
                  )}
                </div>
                <div className="form-field">
                  <label>
                    <FormattedMessage id="ocr_form_contact_indicative_number" defaultMessage="Orientačné číslo" />
                  </label>
                  <input
                    type="text"
                    value={getFormValue('contact_indicative_number')}
                    onChange={(e) => handleOcrChange('contact_indicative_number', e.target.value)}
                    style={ocrErrors['contact_indicative_number'] ? { borderColor: 'red' } : {}}
                  />
                  {ocrErrors['contact_indicative_number'] && (
                    <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                      {Array.isArray(ocrErrors['contact_indicative_number'])
                        ? ocrErrors['contact_indicative_number'].map((msg, idx) => (
                            <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                          ))
                        : <FormattedMessage id={ocrErrors['contact_indicative_number']} defaultMessage={ocrErrors['contact_indicative_number']} />}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>
                    <FormattedMessage id="ocr_form_contact_statement_number" defaultMessage="Súpisné číslo" />
                    <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>
                      (<FormattedMessage id="ocr_form_optional" defaultMessage="nepovinné" />)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={getFormValue('contact_statement_number')}
                    onChange={(e) => handleOcrChange('contact_statement_number', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>
                    <FormattedMessage id="ocr_form_contact_city" defaultMessage="Mesto" />
                  </label>
                  <input
                    type="text"
                    value={getFormValue('contact_city')}
                    onChange={(e) => handleOcrChange('contact_city', e.target.value)}
                    style={ocrErrors['contact_city'] ? { borderColor: 'red' } : {}}
                  />
                  {ocrErrors['contact_city'] && (
                    <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                      {Array.isArray(ocrErrors['contact_city'])
                        ? ocrErrors['contact_city'].map((msg, idx) => (
                            <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                          ))
                        : <FormattedMessage id={ocrErrors['contact_city']} defaultMessage={ocrErrors['contact_city']} />}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>
                    <FormattedMessage id="ocr_form_contact_postal_code" defaultMessage="PSČ" />
                  </label>
                  <input
                    type="text"
                    value={getFormValue('contact_postal_code')}
                    onChange={(e) => handleOcrChange('contact_postal_code', e.target.value)}
                    style={ocrErrors['contact_postal_code'] ? { borderColor: 'red' } : {}}
                  />
                  {ocrErrors['contact_postal_code'] && (
                    <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                      {Array.isArray(ocrErrors['contact_postal_code'])
                        ? ocrErrors['contact_postal_code'].map((msg, idx) => (
                            <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                          ))
                        : <FormattedMessage id={ocrErrors['contact_postal_code']} defaultMessage={ocrErrors['contact_postal_code']} />}
                    </div>
                  )}
                </div>
                <div className="form-field">{/* Prázdne pole pre symetriu */}</div>
              </div>
            </div>
          )}

          {/* Sekcia 3: Údaje o doklade */}
          <div className="form-section">
            <h4 className="section-title">Údaje o doklade</h4>
            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_document_number" defaultMessage="Číslo dokladu" />
                </label>
                <input
                  type="text"
                  value={getFormValue('document_number')}
                  onChange={(e) => handleOcrChange('document_number', e.target.value)}
                  style={ocrErrors['document_number'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['document_number'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['document_number'])
                      ? ocrErrors['document_number'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['document_number']} defaultMessage={ocrErrors['document_number']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_issue_date" defaultMessage="Dátum vydania" />
                </label>
                <input
                  type="text"
                  value={getFormValue('issue_date')}
                  onChange={(e) => handleOcrDateChange('issue_date', e.target.value)}
                  placeholder="dd.mm.yyyy"
                  style={ocrErrors['issue_date'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['issue_date'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['issue_date'])
                      ? ocrErrors['issue_date'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['issue_date']} defaultMessage={ocrErrors['issue_date']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_expiry_date" defaultMessage="Dátum platnosti" />
                </label>
                <input
                  type="text"
                  value={getFormValue('expiry_date')}
                  onChange={(e) => handleOcrDateChange('expiry_date', e.target.value)}
                  placeholder="dd.mm.yyyy"
                  style={ocrErrors['expiry_date'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['expiry_date'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['expiry_date'])
                      ? ocrErrors['expiry_date'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['expiry_date']} defaultMessage={ocrErrors['expiry_date']} />}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_issued_by" defaultMessage="Vydal" />
                </label>
                <input
                  type="text"
                  value={getFormValue('issued_by')}
                  onChange={(e) => handleOcrChange('issued_by', e.target.value)}
                  style={ocrErrors['issued_by'] ? { borderColor: 'red' } : {}}
                />
                {ocrErrors['issued_by'] && (
                  <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                    {Array.isArray(ocrErrors['issued_by'])
                      ? ocrErrors['issued_by'].map((msg, idx) => (
                          <FormattedMessage key={msg + idx} id={msg} defaultMessage={msg} />
                        ))
                      : <FormattedMessage id={ocrErrors['issued_by']} defaultMessage={ocrErrors['issued_by']} />}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row form-row-full">
              <div className="form-field">
                <label>
                  <FormattedMessage id="ocr_form_special_remarks" defaultMessage="Osobitné poznámky" />
                  <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>
                    (<FormattedMessage id="ocr_form_optional" defaultMessage="nepovinné" />)
                  </span>
                </label>
                <input
                  type="text"
                  value={getFormValue('special_remarks')}
                  onChange={(e) => handleOcrChange('special_remarks', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="step-actions">
        <Button type="PRIMARY" onClick={handleOcrStep} disabled={isLoading}>
          {isLoading ? (
            <InlineLoading />
          ) : (
            <FormattedMessage id="online_onboarding_ocr_continue" defaultMessage="Pokračovať" />
          )}
        </Button>
      </div>
    </div>
  );

  const renderIncomeDocsStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_income_docs_title"
          defaultMessage="Dokumenty o príjmoch"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_income_docs_description"
          defaultMessage="Prosím, nahrajte požadované dokumenty o Vašich príjmoch za posledné 2 mesiace."
        />
      </BodyText>

      <div className="step-content">
        <BodyText>
          <FormattedMessage
            id="online_onboarding_income_docs_info"
            defaultMessage="Pre overenie Vašej finančnej situácie potrebujeme nasledujúce dokumenty:"
          />
        </BodyText>

        <div className="documents-upload">
          {(showPayslip1 || showPayslip2) && (
            <div className="document-section">
              <h4>
                <FormattedMessage
                  id="online_onboarding_payslips_title"
                  defaultMessage="Výplatné pásky"
                />
              </h4>
              <BodyText style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                <FormattedMessage
                  id="online_onboarding_payslips_description"
                  defaultMessage="Nahrajte výplatné pásky za posledné 2 mesiace"
                />
              </BodyText>

              <div className="document-row">
                {showPayslip1 && (
                  <div className="document-item">
                    <label>
                      <FormattedMessage
                        id="online_onboarding_payslip_1"
                        defaultMessage="Výplatná páska 1"
                      />
                    </label>
                    <div className="file-upload-container">
                      <button type="button" className="file-upload-button" disabled={isLoading}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        <FormattedMessage
                          id="online_onboarding_choose_file"
                          defaultMessage="Vybrať súbor"
                        />
                      </button>
                      <input
                        key={`payslip1-${payslip1Key}`}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleDocumentCapture(e.target.files[0], 'payslip1')}
                        disabled={isLoading}
                      />
                    </div>
                    {payslip1 && (
                      <div className="file-selected">
                        <span className="file-name" title={payslip1.name}>{payslip1.name}</span>
                        <button
                          type="button"
                          className="file-remove"
                          aria-label="Odstrániť súbor"
                          onClick={() => handleRemoveIncomeDoc('payslip1')}
                          disabled={isLoading}
                          title="Odstrániť"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h.3l.9 12.1A2 2 0 0 0 8.2 21h7.6a2 2 0 0 0 2-1.9L18.7 7H19a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9zm2 2h2v1h-2V5zM9.3 7h5.4l.9 12H8.4L9.3 7zM10 9a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                    {payslip1Error && <div className="error-message">{payslip1Error}</div>}
                  </div>
                )}

                {showPayslip2 && (
                  <div className="document-item">
                    <label>
                      <FormattedMessage
                        id="online_onboarding_payslip_2"
                        defaultMessage="Výplatná páska 2"
                      />
                    </label>
                    <div className="file-upload-container">
                      <button type="button" className="file-upload-button" disabled={isLoading}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        <FormattedMessage
                          id="online_onboarding_choose_file"
                          defaultMessage="Vybrať súbor"
                        />
                      </button>
                      <input
                        key={`payslip2-${payslip2Key}`}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleDocumentCapture(e.target.files[0], 'payslip2')}
                        disabled={isLoading}
                      />
                    </div>
                    {payslip2 && (
                      <div className="file-selected">
                        <span className="file-name" title={payslip2.name}>{payslip2.name}</span>
                        <button
                          type="button"
                          className="file-remove"
                          aria-label="Odstrániť súbor"
                          onClick={() => handleRemoveIncomeDoc('payslip2')}
                          disabled={isLoading}
                          title="Odstrániť"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h.3l.9 12.1A2 2 0 0 0 8.2 21h7.6a2 2 0 0 0 2-1.9L18.7 7H19a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9zm2 2h2v1h-2V5zM9.3 7h5.4l.9 12H8.4L9.3 7zM10 9a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                    {payslip2Error && <div className="error-message">{payslip2Error}</div>}
                  </div>
                )}
              </div>
            </div>
          )}

          {(showBankStatement1 || showBankStatement2) && (
            <div className="document-section">
              <h4>
                <FormattedMessage
                  id="online_onboarding_bank_statements_title"
                  defaultMessage="Výpisy z účtu"
                />
              </h4>
              <BodyText style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                <FormattedMessage
                  id="online_onboarding_bank_statements_description"
                  defaultMessage="Nahrajte výpisy z bankového účtu za posledné 2 mesiace"
                />
              </BodyText>

              <div className="document-row">
                {showBankStatement1 && (
                  <div className="document-item">
                    <label>
                      <FormattedMessage
                        id="online_onboarding_bank_statement_1"
                        defaultMessage="Výpis z účtu 1"
                      />
                    </label>
                    <div className="file-upload-container">
                      <button type="button" className="file-upload-button" disabled={isLoading}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        <FormattedMessage
                          id="online_onboarding_choose_file"
                          defaultMessage="Vybrať súbor"
                        />
                      </button>
                      <input
                        key={`bankStatement1-${bankStatement1Key}`}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleDocumentCapture(e.target.files[0], 'bankStatement1')}
                        disabled={isLoading}
                      />
                    </div>
                    {bankStatement1 && (
                      <div className="file-selected">
                        <span className="file-name" title={bankStatement1.name}>{bankStatement1.name}</span>
                        <button
                          type="button"
                          className="file-remove"
                          aria-label="Odstrániť súbor"
                          onClick={() => handleRemoveIncomeDoc('bankStatement1')}
                          disabled={isLoading}
                          title="Odstrániť"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h.3l.9 12.1A2 2 0 0 0 8.2 21h7.6a2 2 0 0 0 2-1.9L18.7 7H19a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9zm2 2h2v1h-2V5zM9.3 7h5.4l.9 12H8.4L9.3 7zM10 9a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                    {bankStatement1Error && <div className="error-message">{bankStatement1Error}</div>}
                  </div>
                )}

                {showBankStatement2 && (
                  <div className="document-item">
                    <label>
                      <FormattedMessage
                        id="online_onboarding_bank_statement_2"
                        defaultMessage="Výpis z účtu 2"
                      />
                    </label>
                    <div className="file-upload-container">
                      <button type="button" className="file-upload-button" disabled={isLoading}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        <FormattedMessage
                          id="online_onboarding_choose_file"
                          defaultMessage="Vybrať súbor"
                        />
                      </button>
                      <input
                        key={`bankStatement2-${bankStatement2Key}`}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => handleDocumentCapture(e.target.files[0], 'bankStatement2')}
                        disabled={isLoading}
                      />
                    </div>
                    {bankStatement2 && (
                      <div className="file-selected">
                        <span className="file-name" title={bankStatement2.name}>{bankStatement2.name}</span>
                        <button
                          type="button"
                          className="file-remove"
                          aria-label="Odstrániť súbor"
                          onClick={() => handleRemoveIncomeDoc('bankStatement2')}
                          disabled={isLoading}
                          title="Odstrániť"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h.3l.9 12.1A2 2 0 0 0 8.2 21h7.6a2 2 0 0 0 2-1.9L18.7 7H19a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9zm2 2h2v1h-2V5zM9.3 7h5.4l.9 12H8.4L9.3 7zM10 9a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                    {bankStatement2Error && <div className="error-message">{bankStatement2Error}</div>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {(((showPayslip1 && !payslip1) || (showPayslip2 && !payslip2) || (showBankStatement1 && !bankStatement1) || (showBankStatement2 && !bankStatement2))) && (
          <BodyText style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
            <FormattedMessage
              id="online_onboarding_income_docs_requirement"
              defaultMessage="Pre pokračovanie je potrebné nahrať všetky požadované dokumenty"
            />
          </BodyText>
        )}
      </div>

      <div className="step-actions">
        <Button
          type="PRIMARY"
          onClick={handleIncomeDocsStep}
          disabled={
            isLoading ||
            (showPayslip1 && !payslip1) ||
            (showPayslip2 && !payslip2) ||
            (showBankStatement1 && !bankStatement1) ||
            (showBankStatement2 && !bankStatement2)
          }
        >
          <FormattedMessage
            id="online_onboarding_income_docs_continue"
            defaultMessage="Odoslať dokumenty o príjmoch"
          />
        </Button>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' }}>
            <InlineLoading />
          </div>
        )}
      </div>
    </div>
  );

  const renderDocumentsStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage id="online_onboarding_documents_title" defaultMessage="Overenie údajov" />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_documents_description"
          defaultMessage="Prosím, nahrajte požadované dokumenty pre overenie totožnosti."
        />
      </BodyText>

      <div className="step-content">
        <BodyText>
          <FormattedMessage
            id="online_onboarding_documents_info"
            defaultMessage="Pre dokončenie registrácie potrebujeme overiť Vašu totožnosť pomocou nasledujúcich dokumentov."
          />
        </BodyText>
      </div>

      <div className="step-actions">
        <Button type="PRIMARY" onClick={() => {}}>
          <FormattedMessage
            id="online_onboarding_documents_upload"
            defaultMessage="Nahrať dokumenty"
          />
        </Button>
      </div>
    </div>
  );

  const renderWaitingForConfirmationStep = () => (
    <div className="step-container" style={{ whiteSpace: 'pre-line' }}>
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_waiting_for_confirmation_title"
          defaultMessage="Potvrdzovanie žiadosti"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_waiting_for_confirmation_description"
        />
      </BodyText>

      <div className="step-content">
        <div className="waiting-status">
          <div className="status-icon">
            <InlineLoading />
          </div>
          <BodyText style={{ textAlign: 'center', marginBottom: '24px' }}>
            <FormattedMessage
              id="online_onboarding_waiting_confirmation_status"
              defaultMessage="Spracovávame Vašu žiadosť..."
            />
          </BodyText>
        </div>

        <div className="loan-summary">
          <h4>
            <FormattedMessage
              id="online_onboarding_loan_summary_title"
              defaultMessage="Súhrn úveru"
            />
          </h4>

          <div className="summary-grid">
            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_loan_amount"
                  defaultMessage="Požadovaná suma"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.amount
                  ? `${Number(onBoardingStepData.amount).toLocaleString()} €`
                  : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_loan_duration"
                  defaultMessage="Doba splácania"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.months ? `${onBoardingStepData.months} mesiacov` : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_monthly_payment"
                  defaultMessage="Mesačná splátka"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.monthly_payment
                  ? `${Number(onBoardingStepData.monthly_payment).toLocaleString()} €`
                  : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_insurance_monthly"
                  defaultMessage="Mesačná splátka za poistenie"
                />
              </label>
              <div className="value">
                {Number(onBoardingStepData.insurance_amount).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_total_monthly_payment"
                  defaultMessage="Celková mesačná platba"
                />
              </label>
              <div className="value">
                {(
                  Number(onBoardingStepData.monthly_payment) +
                  Number(onBoardingStepData.insurance_amount)
                ).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_guarantee_fund_fee"
                  defaultMessage="Provízia"
                />
              </label>
              <div className="value">
                {Number(onBoardingStepData.fee).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item full-width">
              <label>
                <FormattedMessage
                  id="online_onboarding_total_amount_to_pay"
                  defaultMessage="Celková suma na zaplatenie"
                />
              </label>
              <div className="value total-amount">
                {(
                      (Number(onBoardingStepData.monthly_payment) +
                        Number(onBoardingStepData.insurance_amount)) *
                        Number(onBoardingStepData.months) +
                      Number(onBoardingStepData.fee)
                    ).toLocaleString()} €
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWaitingForDocumentsCheckStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_waiting_for_documents_check_title"
          defaultMessage="Kontrola dokumentov"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_waiting_for_documents_check_description"
        />
      </BodyText>

      <div className="step-content">
        <div className="waiting-status">
          <div className="status-icon">
            <InlineLoading />
          </div>
          <BodyText style={{ textAlign: 'center', marginBottom: '24px' }}>
            <FormattedMessage
              id="online_onboarding_waiting_documents_check_status"
              defaultMessage="Kontrolujeme Vaše dokumenty..."
            />
          </BodyText>
        </div>

        <div className="loan-summary">
          <h4>
            <FormattedMessage
              id="online_onboarding_loan_summary_title"
              defaultMessage="Súhrn úveru"
            />
          </h4>

          <div className="summary-grid">
            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_loan_amount"
                  defaultMessage="Požadovaná suma"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.amount
                  ? `${Number(onBoardingStepData.amount).toLocaleString()} €`
                  : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_loan_duration"
                  defaultMessage="Doba splácania"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.months ? `${onBoardingStepData.months} mesiacov` : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_monthly_payment"
                  defaultMessage="Mesačná splátka"
                />
              </label>
              <div className="value">
                {onBoardingStepData?.monthly_payment
                  ? `${Number(onBoardingStepData.monthly_payment).toLocaleString()} €`
                  : '---'}
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_insurance_monthly"
                  defaultMessage="Mesačná splátka za poistenie"
                />
              </label>
              <div className="value">
                {Number(onBoardingStepData.insurance_amount).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_total_monthly_payment"
                  defaultMessage="Celková mesačná platba"
                />
              </label>
              <div className="value">
                {(
                  Number(onBoardingStepData.monthly_payment) +
                  Number(onBoardingStepData.insurance_amount)
                ).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item">
              <label>
                <FormattedMessage
                  id="online_onboarding_guarantee_fund_fee"
                  defaultMessage="Provízia"
                />
              </label>
              <div className="value">
                {Number(onBoardingStepData.fee).toLocaleString()} €
              </div>
            </div>

            <div className="summary-item full-width">
              <label>
                <FormattedMessage
                  id="online_onboarding_total_amount_to_pay"
                  defaultMessage="Celková suma na zaplatenie"
                />
              </label>
              <div className="value total-amount">
                {(
                      (Number(onBoardingStepData.monthly_payment) +
                        Number(onBoardingStepData.insurance_amount)) *
                        Number(onBoardingStepData.months) +
                      Number(onBoardingStepData.fee)
                    ).toLocaleString()} €
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoanDocumentationStep = () => {
    return (
      <div className="step-container">
        <HeadingThree>
          <FormattedMessage
            id="online_onboarding_loan_documentation_title"
            defaultMessage="Dokumentácia úveru"
          />
        </HeadingThree>
        <div className="documents-agreement">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={agreesToDocuments}
              onChange={(e) => setAgreesToDocuments(e.target.checked)}
            />
            <span className="checkmark"></span>
            <span className="checkbox-text">
              <FormattedMessage
                id="online_onboarding_loan_documents_agreement"
                values={{
                  link: (
                    <a
                      href="https://www.wishmaker.sk/dokumenty"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      www.wishmaker.sk/dokumenty
                    </a>
                  ),
                }}
              />
            </span>
          </label>
        </div>
        <div className="step-content">
          <BodyText>
            <FormattedMessage
              id="online_onboarding_loan_documentation_info"
            />
          </BodyText>
          {/* Odkaz na otvorenie všetkých dokumentov v modale */}
          {loanDocuments.length > 0 && (
            <div style={{ margin: '16px 0' }}>
              <button
                type="button"
                style={{
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={() => {
                  setDocsModalIndex(0);
                  setDocsModalOpen(true);
                }}
              >
                <FormattedMessage id="global_open_all_docs" />
              </button>
            </div>
          )}

          <div className="documents-verification">
            {loanDocuments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <InlineLoading />
                <BodyText style={{ marginTop: '16px' }}>
                  <FormattedMessage
                    id="online_onboarding_loan_documents_loading"
                    defaultMessage="Načítavame dokumenty..."
                  />
                </BodyText>
              </div>
            ) : (
              loanDocuments.map((doc, idx) => {
                const isMobile = window.innerWidth <= 768;
                return (
                  <div
                    key={doc.id}
                    className={`document-check ${doc.active ? 'active' : ''}`}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => !isMobile && setHoveredDoc(idx)}
                    onMouseLeave={() => !isMobile && setHoveredDoc(null)}
                    onClick={() => {
                      // Na mobile: toggle active state ak je dokument už active
                      if (isMobile && doc.active) {
                        setLoanDocuments((prevDocs) => {
                          const newDocs = [...prevDocs];
                          newDocs[idx].active = false;
                          return newDocs;
                        });
                        // Nastav areAllDocumentsRead na false len ak ide o povinný dokument
                        if (doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf') {
                          setAreAllDocumentsRead(false);
                        }
                      } else {
                        // Na desktop alebo mobile ak nie je active: otvor dokument
                        handleDocumentClick(doc.id);
                      }
                    }}
                  >
                    <div className="status-box"></div>
                    <div
                      className="document-link"
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        minHeight: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="pdf-icon">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                      <span>{doc.display_name || doc.name}</span>
                    </div>
                    {!isMobile && hoveredDoc === idx && (
                      <button
                        type="button"
                        style={{
                          background: '#f3f3f3',
                          color: '#888',
                          borderRadius: '12px',
                          border: 'none',
                          fontWeight: 600,
                          padding: '2px 14px',
                          marginLeft: '12px',
                          cursor: 'pointer',
                          boxShadow: 'none',
                          zIndex: 2,
                          height: '24px',
                          minHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLoanDocuments((prevDocs) => {
                            const newDocs = [...prevDocs];
                            newDocs[idx].active = false;
                            return newDocs;
                          });
                          // Nastav areAllDocumentsRead na false len ak ide o povinný dokument
                          if (doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf') {
                            setAreAllDocumentsRead(false);
                          }
                        }}
                      >
                        Nesúhlasím
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="step-actions">
          <Button
            type="PRIMARY"
            onClick={handleLoanDocumentationContinue}
            disabled={!areAllDocumentsRead || !agreesToDocuments}
          >
            <FormattedMessage
              id="online_onboarding_loan_documentation_continue"
              defaultMessage="Pokračovať"
            />
          </Button>
        </div>
      </div>
    );
  };

  const renderInsuranceDocumentationStep = () => {
    return (
      <div className="step-container">
        <HeadingThree>Poistná dokumentácia</HeadingThree>
        <div className="documents-agreement">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={agreesToDocuments}
              onChange={(e) => setAgreesToDocuments(e.target.checked)}
            />
            <span className="checkmark"></span>
            <span className="checkbox-text">
              <FormattedMessage
                id="online_onboarding_insurance_documents_agreement"
                values={{
                  link: (
                    <a
                      href="https://www.wishmaker.sk/dokumenty"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      www.wishmaker.sk/dokumenty
                    </a>
                  ),
                }}
              />
            </span>
          </label>
        </div>
        <div className="step-content">
          <BodyText>
            <FormattedMessage
              id="online_onboarding_loan_documentation_info"
            />
          </BodyText>
          {loanDocuments.length > 0 && (
            <div style={{ margin: '16px 0' }}>
              <button
                type="button"
                style={{
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
                onClick={() => {
                  setDocsModalIndex(0);
                  setDocsModalOpen(true);
                }}
              >
                <FormattedMessage id="global_open_all_docs" />
              </button>
            </div>
          )}
          <div className="documents-verification">
            {loanDocuments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <InlineLoading />
                <BodyText style={{ marginTop: '16px' }}>Načítavame poistné dokumenty...</BodyText>
              </div>
            ) : (
              loanDocuments.map((doc, idx) => {
                const isMobile = window.innerWidth <= 768;
                return (
                  <div
                    key={doc.id}
                    className={`document-check ${doc.active ? 'active' : ''}`}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => !isMobile && setHoveredDoc(idx)}
                    onMouseLeave={() => !isMobile && setHoveredDoc(null)}
                    onClick={() => {
                      // Na mobile: toggle active state ak je dokument už active
                      if (isMobile && doc.active) {
                        setLoanDocuments((prevDocs) => {
                          const newDocs = [...prevDocs];
                          newDocs[idx].active = false;
                          return newDocs;
                        });
                        // Nastav areAllDocumentsRead na false len ak ide o povinný dokument
                        if (doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf') {
                          setAreAllDocumentsRead(false);
                        }
                      } else {
                        // Na desktop alebo mobile ak nie je active: otvor dokument
                        handleDocumentClick(doc.id);
                      }
                    }}
                  >
                    <div className="status-box"></div>
                    <div
                      className="document-link"
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        minHeight: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="pdf-icon">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                      <span>{doc.display_name || doc.name}</span>
                    </div>
                    {!isMobile && hoveredDoc === idx && (
                      <button
                        type="button"
                        style={{
                          background: '#f3f3f3',
                          color: '#888',
                          borderRadius: '12px',
                          border: 'none',
                          fontWeight: 600,
                          padding: '2px 14px',
                          marginLeft: '12px',
                          cursor: 'pointer',
                          boxShadow: 'none',
                          zIndex: 2,
                          height: '24px',
                          minHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLoanDocuments((prevDocs) => {
                            const newDocs = [...prevDocs];
                            newDocs[idx].active = false;
                            return newDocs;
                          });
                          // Nastav areAllDocumentsRead na false len ak ide o povinný dokument
                          if (doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf') {
                            setAreAllDocumentsRead(false);
                          }
                        }}
                      >
                        Nesúhlasím
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="step-actions">
          <Button
            type="PRIMARY"
            onClick={handleLoanDocumentationContinue}
            disabled={!areAllDocumentsRead || !agreesToDocuments}
          >
            Pokračovať
          </Button>
        </div>
      </div>
    );
  };

  const renderOnlineLoanFinishedStep = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_online_loan_finished_title"
          defaultMessage="Žiadosť dokončená"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_online_loan_finished_description"
          defaultMessage="Dokončili ste všetky kroky online pôžičky. Vaše údaje a dokumenty boli úspešne odoslané. Ak sa stránka automaticky neobnoví, kliknite na tlačidlo nižšie."
        />
      </BodyText>
      <div className="step-actions">
        <Button type="PRIMARY" onClick={() => window.location.reload()}>
          <FormattedMessage
            id="online_onboarding_online_loan_finished_go_to_loan"
            defaultMessage="Prejsť na úver"
          />
        </Button>
        <BodyText
          style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px', color: '#666' }}
        >
          <FormattedMessage
            id="online_onboarding_online_loan_finished_auto_redirect"
            defaultMessage="Automaticky Vás presmerujeme o {seconds} sekúnd..."
            values={{ seconds: redirectCountdown }}
          />
        </BodyText>
      </div>
    </div>
  );

  const renderStepCompleted = () => (
    <div className="step-container">
      <HeadingThree>
        <FormattedMessage
          id="online_onboarding_step_completed_title"
          defaultMessage="Krok dokončený"
        />
      </HeadingThree>
      <BodyText>
        <FormattedMessage
          id="online_onboarding_step_completed_description"
          defaultMessage="Prvý krok online registrácie bol úspešne dokončený."
        />
      </BodyText>
    </div>
  );

  // Function to mark current document as read and move to next
  const handleDocModalNext = () => {
    // Mark current document as read
    const updatedDocuments = loanDocuments.map((doc, index) =>
      index === docsModalIndex ? { ...doc, active: true } : doc
    );
    setLoanDocuments(updatedDocuments);

    // Check if all required documents are read
    const requiredDocsRead = updatedDocuments
      .filter((doc) => doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf')
      .every((doc) => doc.active === true);
    if (requiredDocsRead) {
      setAreAllDocumentsRead(true);
    }

    // Move to next document
    setDocsModalIndex((i) => Math.min(i + 1, loanDocuments.length - 1));
  };

  // Function to close document modal without marking documents as read
  const closeDocsModal = () => {
    setDocsModalOpen(false);
  };

  // Function to close document modal and mark current/all documents as read
  const closeDocsModalAndMarkAllRead = () => {
    // Mark current document as read (or all if it's the last one)
    const updatedDocuments = loanDocuments.map((doc, index) =>
      index === docsModalIndex ? { ...doc, active: true } : doc
    );
    setLoanDocuments(updatedDocuments);

    // Check if all required documents are read
    const requiredDocsRead = updatedDocuments
      .filter((doc) => doc.name !== 'navrh_dohody_o_zrazkach_zo_mzdy_dlznik.pdf')
      .every((doc) => doc.active === true);
    if (requiredDocsRead) {
      setAreAllDocumentsRead(true);
    }

    setDocsModalOpen(false);
  };

  // Render document modal
  const renderDocsModal = () => {
    if (!isDocsModalOpen) return null;
    const doc = loanDocuments[docsModalIndex];
    if (!doc) return null;
    const token = localStorage.getItem(LS_ACCESS_TOKEN);
    const fullUrl = `${process.env.REACT_APP_WISHMAKER_API_URI}/user-area/storage/${doc.url}?token=${token}`;

    return (
      <DocsModal
        isOpened={isDocsModalOpen}
        onClose={closeDocsModal}
        doc={{ ...doc, fullUrl }}
        docsModalIndex={docsModalIndex}
        docsCount={loanDocuments.length}
        onPrev={() => setDocsModalIndex((i) => Math.max(i - 1, 0))}
        onNext={handleDocModalNext}
        onFinish={closeDocsModalAndMarkAllRead}
      />
    );
  };

  // Render insurance modal (insurance selection before submitting income-docs)
  const calculateInsuranceCost = (variant) => {
    if (!variant || !variant.actual_validity) return null;
    const validity = variant.actual_validity;
    const loanAmount = Number(onBoardingStepData?.loan_amount || onBoardingStepData?.amount);
    if (!loanAmount && validity.value_type === 'percentage') return null;
    if (validity.value_type === 'percentage') {
      return (Number(validity.value) / 100) * loanAmount;
    }
    return Number(validity.value);
  };

  const formatCost = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${numeral(value).format('0,0.00')} €`;
  };

  const handleInsuranceConfirm = () => {
    if (selectedInsuranceVariant === null) return; // nič nevybrané
    setInsuranceModalOpen(false);
    // Po zatvorení modalu ihneď pokračuj v odoslaní dokumentov (insured flag sa aktualizuje až po submit-e)
    setTimeout(() => {
      submitIncomeDocs();
    }, 0);
  };

  const renderInsuranceModal = () => {
    if (!isInsuranceModalOpen) return null;

    const backendVariants = Array.isArray(onBoardingStepData?.insurance_variants)
      ? onBoardingStepData.insurance_variants.filter(v => v && v.active)
      : [];

    // Build list of cards + option without insurance
    const cards = [
      ...backendVariants.map(v => ({
        key: v.id,
        variant: v,
        name: v.name,
        description: v.description,
        cost: calculateInsuranceCost(v),
      })),
      { key: 'none', name: 'Bez poistenia', description: 'Pokračovať bez poistenia', cost: 0 },
    ];

    const handleSelect = (key) => {
      // Single selection behavior (checkbox UI): always set selected key; allow deselect to null if same clicked
      if (selectedInsuranceVariant === key) {
        setSelectedInsuranceVariant(null); // umožní používateľovi zrušiť výber ak klikne opäť (ak to nechceme, odstrániť tento blok)
      } else {
        setSelectedInsuranceVariant(key);
      }
    };

    const priceLabel = (c) => {
      if (c.key === 'none') return '0 €';
      const base = formatCost(c.cost);
      return base === '-' ? '-' : `${base} / mes.`;
    };

    return (
      <WishmakerModal
        isOpened={isInsuranceModalOpen}
        onClose={() => {
          setInsuranceModalOpen(false);
        }}
        withCross
        className="special"
      >
        <div style={{ padding: '24px', maxWidth: '640px' }}>
          <HeadingThree style={{ marginBottom: '12px' }}>
            <FormattedMessage id="online_onboarding_insurance_select_title" />
          </HeadingThree>
          <BodyText style={{ marginBottom: '20px', fontSize: '14px', lineHeight: 1.5 }}>
            <FormattedMessage id="online_onboarding_insurance_select_description" />
          </BodyText>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
            {cards.map(c => {
              const checked = selectedInsuranceVariant === c.key;
              return (
                <label key={c.key} style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', fontWeight: checked ? 600 : 400 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleSelect(c.key)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                      aria-label={c.name}
                    />
                    <span style={{ fontSize: '15px' }}>{c.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '14px', color: '#222' }}>{priceLabel(c)}</span>
                  </div>
                  <span style={{ minHeight: '18px', marginLeft: '28px', fontSize: '12px', color: '#555', fontWeight: 400, display: 'block' }}>
                    {c.description ? c.description : '\u00A0'}
                  </span>
                </label>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <Button
              type="PRIMARY"
              onClick={handleInsuranceConfirm}
              disabled={selectedInsuranceVariant === null}
            >
              <FormattedMessage id="online_onboarding_insurance_confirm" />
            </Button>
          </div>
        </div>
      </WishmakerModal>
    );
  };

  // Helper: build mobile signing URL (prefer from backend if available)
  const backendMobileUrl = onBoardingStepData?.mobile_signature_url || onBoardingStepData?.signature_url;
  const fallbackOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
  const mobileSignUrl = backendMobileUrl || `${fallbackOrigin}/mobile-signature`;

  // Zobraz krok presne podľa premennej step z backendu
  const displayStep = onBoardingStepData?.step || onBoardingStep || currentStep;

  return (
    <ContentWrap>
      <OnlineOnboardingContentSC>
        <div className="online-onboarding-wrap">
          {/* Ak je špeciálny stav, zobraz len info o stave */}
          {specialStateName
            ? renderSpecialState(specialStateName)
            : (
              <>
                {displayStep === 'id-card' && renderIdCardStep()}
                {displayStep === 'ocr' && renderOcrStep()}
                {displayStep === 'income-docs' && renderIncomeDocsStep()}
                {displayStep === 'get-loan-documentation' && renderLoanDocumentationStep()}
                {displayStep === 'get-insurance-documentation' && renderInsuranceDocumentationStep()}
                {displayStep === 'waiting-for-documents-check' && renderWaitingForDocumentsCheckStep()}
                {displayStep === 'online-loan-finished' && renderOnlineLoanFinishedStep()}
                {displayStep === 'waiting-for-confirmation' && renderWaitingForConfirmationStep()}
                {displayStep === 'documents' && renderDocumentsStep()}
                {displayStep === 'step-completed' && renderStepCompleted()}
              </>
            )}
        </div>
        {renderDocsModal()}
        <SignatureModal
          isOpened={isSignatureModalOpen}
          onClose={() => setSignatureModalOpen(false)}
          onSubmit={handleSignatureSubmit}
          mobileSignUrl={mobileSignUrl}
          preLoanId={onBoardingStepData?.id}
        />
        {renderInsuranceModal()}
      </OnlineOnboardingContentSC>
    </ContentWrap>
  );
};

const mapStateToProps = (state) => {
  return {
    userType: state.user.type,
    onBoardingStep: state.onboarding.step,
    onBoardingStepData: state.onboarding,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getBorrowerOnboardingStep: ActionCreators.getBorrowerOnboardingStep,
      sendBorrowerOnboardingDocuments: ActionCreators.sendBorrowerOnboardingDocuments,
      sendBorrowerOnboardingStep: ActionCreators.sendBorrowerOnboardingStep,
    },
    dispatch
  );
};

const OnlineOnboardingContent = connect(
  mapStateToProps,
  mapDispatchToProps
)(_OnlineOnboardingContent);

export default OnlineOnboardingContent;

